import {
  MAX_DECODED_PIXELS,
  MAX_IMAGE_BYTES,
  MAX_MEGAPIXELS,
  MAX_PDF_BYTES,
} from './constants';
import {
  extensionOf,
  isSupportedExtension,
  isSupportedMime,
  readHeaderBytes,
  sniffFileType,
  type SniffedType,
} from './fileTypes';
import { OcrError } from './types';

export interface PreliminaryResult {
  accepted: boolean;
  reason?: string;
  code?: string;
}

/**
 * Stage 1 — synchronous, permissive. A file is provisionally accepted when
 * either its extension or its browser-reported MIME type is supported.
 * WhatsApp JPEGs frequently report `image/jpg` or an empty MIME type.
 */
export function preliminaryCheck(file: { name: string; type: string; size: number }): PreliminaryResult {
  const ext = extensionOf(file.name);
  const mime = (file.type || '').toLowerCase();
  const extOk = isSupportedExtension(file.name);
  const mimeOk = isSupportedMime(mime);
  const emptyMimeWithGoodExt = mime === '' && extOk;

  if (!extOk && !mimeOk && !emptyMimeWithGoodExt) {
    return {
      accepted: false,
      code: 'UNSUPPORTED_FORMAT',
      reason: `“${file.name}” is not a supported file. Use JPG, JPEG, PNG, WEBP, BMP or PDF.`,
    };
  }

  if (file.size === 0) {
    return { accepted: false, code: 'EMPTY_FILE', reason: `“${file.name}” is empty (0 bytes).` };
  }

  const looksLikePdf = ext === 'pdf' || mime === 'application/pdf';
  const limit = looksLikePdf ? MAX_PDF_BYTES : MAX_IMAGE_BYTES;
  if (file.size > limit) {
    const mb = (limit / (1024 * 1024)).toFixed(0);
    return {
      accepted: false,
      code: 'FILE_TOO_LARGE',
      reason: `“${file.name}” is ${(file.size / (1024 * 1024)).toFixed(1)} MB. The limit for ${
        looksLikePdf ? 'PDFs' : 'images'
      } is ${mb} MB.`,
    };
  }

  return { accepted: true };
}

export interface DecodedSize {
  width: number;
  height: number;
}

export type ImageProbe = (file: Blob) => Promise<DecodedSize>;

export const browserImageProbe: ImageProbe = async (file) => {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close?.();
    return size;
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const size = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(size);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The browser could not decode this image.'));
    };
    img.src = url;
  });
};

export interface ValidatedFile {
  kind: 'image' | 'pdf';
  sniffed: SniffedType;
  width?: number;
  height?: number;
}

/**
 * Stage 2 — authoritative. Real header bytes plus a successful decode decide.
 */
export async function validateFile(
  file: File | Blob & { name: string; type: string; size: number },
  probe: ImageProbe = browserImageProbe,
): Promise<ValidatedFile> {
  const named = file as File;
  const pre = preliminaryCheck({ name: named.name, type: named.type, size: named.size });
  if (!pre.accepted) {
    throw new OcrError((pre.code as never) || 'UNSUPPORTED_FORMAT', pre.reason as string);
  }

  let header: Uint8Array;
  try {
    header = await readHeaderBytes(file, 1024);
  } catch (e) {
    throw new OcrError('CORRUPT_FILE', `“${named.name}” could not be read from disk.`);
  }

  const sniffed = sniffFileType(header);
  if (!sniffed) {
    throw new OcrError(
      'INVALID_SIGNATURE',
      `“${named.name}” does not contain a valid JPG, PNG, WEBP, BMP or PDF. The file may be renamed or damaged.`,
    );
  }

  if (sniffed === 'pdf') {
    if (named.size > MAX_PDF_BYTES) {
      throw new OcrError('FILE_TOO_LARGE', `“${named.name}” exceeds the 20 MB PDF limit.`);
    }
    return { kind: 'pdf', sniffed };
  }

  if (named.size > MAX_IMAGE_BYTES) {
    throw new OcrError('FILE_TOO_LARGE', `“${named.name}” exceeds the 10 MB image limit.`);
  }

  let size: DecodedSize;
  try {
    size = await probe(file);
  } catch (e) {
    throw new OcrError(
      'DECODE_FAILED',
      `“${named.name}” could not be decoded. The image is corrupt or uses an unsupported variant.`,
      (e as Error)?.message,
    );
  }

  if (!size || !size.width || !size.height) {
    throw new OcrError('EMPTY_IMAGE', `“${named.name}” decoded to an empty image (zero width or height).`);
  }

  if (size.width * size.height > MAX_DECODED_PIXELS) {
    const mp = (size.width * size.height) / 1_000_000;
    throw new OcrError(
      'IMAGE_TOO_LARGE',
      `“${named.name}” is ${mp.toFixed(1)} megapixels. The limit is ${MAX_MEGAPIXELS} megapixels.`,
    );
  }

  return { kind: 'image', sniffed, width: size.width, height: size.height };
}
